import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ArtistService from "../../../Services/artists/ArtistService";
import LabelService from "../../../Services/label/LabelService";
import YoutubeService from "../../../Services/youtube/YoutubeService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

const MONTH_OPTIONS = [
  { value: "january", label: "January" },
  { value: "february", label: "February" },
  { value: "march", label: "March" },
  { value: "april", label: "April" },
  { value: "may", label: "May" },
  { value: "june", label: "June" },
  { value: "july", label: "July" },
  { value: "august", label: "August" },
  { value: "september", label: "September" },
  { value: "october", label: "October" },
  { value: "november", label: "November" },
  { value: "december", label: "December" },
];

function defaultMonthValue() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return MONTH_OPTIONS[date.getMonth()]?.value ?? "january";
}

function defaultYearValue() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return String(date.getFullYear());
}

function YoutubeRevenueDispatchModal({ isOpen, toggle, onDispatched }) {
  const [scope, setScope] = useState("all");
  const [month, setMonth] = useState(defaultMonthValue);
  const [year, setYear] = useState(defaultYearValue);
  const [labelId, setLabelId] = useState("");
  const [artistSearch, setArtistSearch] = useState("");
  const [selectedArtistIds, setSelectedArtistIds] = useState([]);
  const [runMode, setRunMode] = useState("sync");

  const labelsQuery = useQuery({
    queryKey: ["labels"],
    queryFn: LabelService.getLabels,
    enabled: isOpen,
  });

  const artistsQuery = useQuery({
    queryKey: ["artists"],
    queryFn: ArtistService.getAllArtists,
    enabled: isOpen,
  });

  const labels = labelsQuery.data?.data ?? [];
  const artists = useMemo(
    () => (artistsQuery.data?.data ?? []).filter((artist) => artist.red_artist_id),
    [artistsQuery.data]
  );

  const filteredArtists = useMemo(() => {
    const term = artistSearch.trim().toLowerCase();
    if (!term) return artists;

    return artists.filter((artist) =>
      [artist.artist_name, artist.red_artist_id, artist.label_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [artists, artistSearch]);

  const showSyncResults = (response) => {
    const results = Array.isArray(response?.results) ? response.results : [];
    const failed = results.filter((row) => row.status === "failed");
    const succeeded = results.filter((row) => row.status === "succeeded");
    const skipped = results.filter((row) => row.status === "skipped");

    if (failed.length > 0) {
      const details = failed
        .map((row) => `${row.artist_name} (${row.red_artist_id}): ${row.error}`)
        .join("\n");

      Swal.fire({
        title: "YouTube sync failed",
        html: `<p class="text-start mb-2">No revenue data was saved. The ArtistSync gateway returned an error when calling Google YouTube APIs.</p><pre class="text-start small" style="white-space:pre-wrap;max-height:240px;overflow:auto">${details}</pre>`,
        icon: "error",
      });
      return;
    }

    if (succeeded.length > 0) {
      const summary = succeeded
        .map((row) => `${row.artist_name}: ${row.videos_synced ?? 0} video(s)`)
        .join(", ");
      toast.success(`YouTube sync complete — ${summary}`);
      onDispatched?.();
      toggle();
      return;
    }

    if (skipped.length > 0) {
      const summary = skipped
        .map((row) => `${row.artist_name}: ${row.reason}`)
        .join("\n");
      Swal.fire({
        title: "No artists synced",
        text: summary,
        icon: "warning",
      });
      return;
    }

    toast.info("Sync finished with no changes.");
  };

  const dispatchMutation = useMutation({
    mutationFn: YoutubeService.dispatchRevenueJobs,
    onSuccess: (response) => {
      if (response?.mode === "sync") {
        showSyncResults(response);
        return;
      }

      const dispatched = response?.dispatched ?? 0;
      const skipped = response?.skipped ?? 0;
      toast.success(
        `YouTube sync jobs queued: ${dispatched} dispatched, ${skipped} skipped. Check logs if no data appears.`
      );
      onDispatched?.();
      toggle();
    },
    onError: (error) => {
      const payload = error.response?.data;
      if (payload?.mode === "sync" && Array.isArray(payload.results)) {
        showSyncResults(payload);
        return;
      }

      toast.error(
        `Failed to dispatch YouTube jobs: ${getApiErrorMessage(
          error,
          "Request failed"
        )}`
      );
    },
  });

  const resetForm = () => {
    setScope("all");
    setMonth(defaultMonthValue());
    setYear(defaultYearValue());
    setLabelId("");
    setArtistSearch("");
    setSelectedArtistIds([]);
    setRunMode("sync");
  };

  const handleToggle = () => {
    if (!dispatchMutation.isPending) {
      if (!isOpen) {
        resetForm();
      }
      toggle();
    }
  };

  const toggleArtist = (redArtistId) => {
    setSelectedArtistIds((current) =>
      current.includes(redArtistId)
        ? current.filter((id) => id !== redArtistId)
        : [...current, redArtistId]
    );
  };

  const canSubmit =
    !dispatchMutation.isPending &&
    month &&
    year &&
    (scope === "all" ||
      (scope === "label" && labelId) ||
      (scope === "artists" && selectedArtistIds.length > 0));

  const handleSubmit = () => {
    const payload = {
      scope,
      month,
      year: Number(year),
      mode: scope === "artists" ? "sync" : runMode,
    };

    if (scope === "label") {
      payload.label_id = Number(labelId);
    }

    if (scope === "artists") {
      payload.red_artist_ids = selectedArtistIds;
    }

    dispatchMutation.mutate(payload);
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="lg">
      <ModalHeader toggle={handleToggle}>Dispatch YouTube revenue sync</ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Queue jobs to pull YouTube revenue into accounting. Jobs run in the
          background — ensure a queue worker is running.
        </p>

        <FormGroup>
          <Label for="youtube-dispatch-scope">Scope</Label>
          <Input
            type="select"
            id="youtube-dispatch-scope"
            value={scope}
            onChange={(event) => setScope(event.target.value)}
          >
            <option value="all">All labels &amp; artists</option>
            <option value="label">One label</option>
            <option value="artists">Selected artists</option>
          </Input>
        </FormGroup>

        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <Label for="youtube-dispatch-month">Month</Label>
              <Input
                type="select"
                id="youtube-dispatch-month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              >
                {MONTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="youtube-dispatch-year">Year</Label>
              <Input
                type="number"
                id="youtube-dispatch-year"
                value={year}
                onChange={(event) => setYear(event.target.value)}
                min="2020"
                max={new Date().getFullYear() + 1}
              />
            </FormGroup>
          </div>
        </div>

        {scope !== "artists" && (
          <FormGroup>
            <Label for="youtube-dispatch-mode">Run mode</Label>
            <Input
              type="select"
              id="youtube-dispatch-mode"
              value={runMode}
              onChange={(event) => setRunMode(event.target.value)}
            >
              <option value="queue">Queue in background (requires queue worker)</option>
              <option value="sync">Run now and show errors</option>
            </Input>
          </FormGroup>
        )}

        {scope === "label" && (
          <FormGroup>
            <Label for="youtube-dispatch-label">Label</Label>
            <Input
              type="select"
              id="youtube-dispatch-label"
              value={labelId}
              onChange={(event) => setLabelId(event.target.value)}
            >
              <option value="">Select label</option>
              {labels.map((label) => (
                <option key={label.id} value={label.id}>
                  {label.name ?? label.label_name ?? `Label #${label.id}`}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}

        {scope === "artists" && (
          <FormGroup>
            <Label>Artists</Label>
            <Input
              type="search"
              placeholder="Search artists…"
              value={artistSearch}
              onChange={(event) => setArtistSearch(event.target.value)}
              className="mb-2"
            />
            <div
              className="border rounded p-2"
              style={{ maxHeight: 220, overflowY: "auto" }}
            >
              {artistsQuery.isPending && (
                <div className="text-muted small">Loading artists…</div>
              )}
              {!artistsQuery.isPending && filteredArtists.length === 0 && (
                <div className="text-muted small">No artists found.</div>
              )}
              {filteredArtists.map((artist) => {
                const id = String(artist.red_artist_id);
                const inputId = `youtube-artist-${id}`;

                return (
                  <div key={id} className="form-check">
                    <Input
                      type="checkbox"
                      id={inputId}
                      className="form-check-input"
                      checked={selectedArtistIds.includes(id)}
                      onChange={() => toggleArtist(id)}
                    />
                    <Label for={inputId} className="form-check-label">
                      {artist.artist_name}
                      <span className="text-muted small ms-1">
                        ({id}
                        {artist.label_name ? ` · ${artist.label_name}` : ""}
                        {artist.youtube_connected ? " · connected" : ""})
                      </span>
                    </Label>
                  </div>
                );
              })}
            </div>
            <div className="text-muted small mt-1">
              {selectedArtistIds.length} artist(s) selected
            </div>
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleToggle} disabled={dispatchMutation.isPending}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {dispatchMutation.isPending ? "Dispatching…" : "Dispatch jobs"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default YoutubeRevenueDispatchModal;
